import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";
import { setAppLoading } from "../../redux/app.reducer";
import {
  getPendingTransfersApi,
  triggerBatchTransferApi,
  cancelPendingTransferApi,
} from "../../api/wallet";
import {
  IPendingTransferGroup,
  IPendingTransferSummary,
  IBatchTransferResult,
} from "../../interface/wallet";
import { moneyDisplay } from "../../utils/money";

const WithdrawPendingTransfer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<IPendingTransferGroup[]>([]);
  const [summary, setSummary] = useState<IPendingTransferSummary>({
    totalUsers: 0,
    totalTransactions: 0,
    totalAmount: 0,
  });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [batchResult, setBatchResult] = useState<IBatchTransferResult | null>(
    null
  );

  const fetchPendingTransfers = async () => {
    try {
      setLoading(true);
      const { data } = await getPendingTransfersApi();
      setGroups(data.results ?? []);
      setSummary(
        data.summary ?? {
          totalUsers: 0,
          totalTransactions: 0,
          totalAmount: 0,
        }
      );
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const handleTriggerBatchTransfer = async () => {
    try {
      setLoading(true);
      setShowConfirmModal(false);
      const { data } = await triggerBatchTransferApi();
      setBatchResult(data);
      setShowResultModal(true);
      await fetchPendingTransfers();
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const handleCancelPendingTransfer = async (walletId: string) => {
    try {
      setLoading(true);
      await cancelPendingTransferApi(walletId);
      openSnackbar("ยกเลิกรายการรอโอนสำเร็จ");
      await fetchPendingTransfers();
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      let msg: string | undefined;
      if (typeof e?.response?.data?.message === "string") {
        msg = e?.response?.data?.message;
      }
      openSnackbar(msg || "Error: Something wrong.");
    }
  };

  const toggleExpand = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  useEffect(() => {
    fetchPendingTransfers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(setAppLoading(loading));
    // eslint-disable-next-line
  }, [loading]);

  return (
    <>
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5 className="card-title">จำนวนผู้ใช้</h5>
              <h2 className="mb-0">
                {summary.totalUsers.toLocaleString("en-US")}
              </h2>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5 className="card-title">จำนวนรายการ</h5>
              <h2 className="mb-0">
                {summary.totalTransactions.toLocaleString("en-US")}
              </h2>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h5 className="card-title">ยอดรวม</h5>
              <h2 className="mb-0">{moneyDisplay(summary.totalAmount)}</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="mb-0">รายการรอโอน</h3>
            <button
              className="btn btn-success btn-lg"
              disabled={groups.length === 0}
              onClick={() => setShowConfirmModal(true)}
            >
              <i className="fas fa-paper-plane mr-2"></i>
              โอนเงินทั้งหมด
            </button>
          </div>

          {groups.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-inbox text-muted"
                style={{ fontSize: "4rem" }}
              ></i>
              <p className="text-muted mt-3">ไม่มีรายการรอโอน</p>
            </div>
          ) : (
            <div className="accordion" id="pendingTransferAccordion">
              {groups.map((group, index) => {
                const groupKey = `${group.userId}-${group.bankAccountId}`;
                const isExpanded = expandedGroups.has(groupKey);

                return (
                  <div className="card mb-2" key={groupKey}>
                    <div
                      className="card-header d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleExpand(groupKey)}
                    >
                      <div className="d-flex align-items-center">
                        <i
                          className={`fas fa-chevron-${
                            isExpanded ? "down" : "right"
                          } mr-3`}
                        ></i>
                        <div>
                          <strong
                            className="hover-underline cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `${process.env.PUBLIC_URL}/drivers/${group.userId}/profile`
                              );
                            }}
                          >
                            {group.userName}
                          </strong>
                          <div className="text-muted small">
                            {group.bankName} - {group.accountNumber} (
                            {group.accountName})
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="badge badge-primary mr-2">
                          {group.walletCount} รายการ
                        </div>
                        <strong className="text-success">
                          {moneyDisplay(Math.abs(group.totalAmount))}
                        </strong>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="card-body">
                        <table className="table table-sm table-bordered mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th style={{ width: "120px" }}>Transaction No.</th>
                              <th style={{ width: "180px" }}>เวลา</th>
                              <th style={{ width: "150px" }}>จำนวนเงิน</th>
                              <th style={{ width: "100px" }}>ดำเนินการ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.wallets.map((wallet) => (
                              <tr key={wallet.id}>
                                <td>{wallet.code}</td>
                                <td>
                                  {moment(wallet.createdAt).format(
                                    "DD/MM/yyyy HH:mm:ss"
                                  )}
                                </td>
                                <td className="text-success">
                                  {moneyDisplay(Math.abs(wallet.amount))}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-outline-warning btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        window.confirm(
                                          "ต้องการยกเลิกรายการนี้ใช่หรือไม่?"
                                        )
                                      ) {
                                        handleCancelPendingTransfer(wallet.id);
                                      }
                                    }}
                                  >
                                    ยกเลิก
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">ยืนยันการโอนเงิน</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowConfirmModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>คุณต้องการโอนเงินทั้งหมดใช่หรือไม่?</p>
                <div className="alert alert-info">
                  <div className="d-flex justify-content-between">
                    <span>จำนวนผู้ใช้:</span>
                    <strong>{summary.totalUsers} คน</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>จำนวนรายการ:</span>
                    <strong>{summary.totalTransactions} รายการ</strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>ยอดรวม:</span>
                    <strong className="text-success">
                      {moneyDisplay(summary.totalAmount)}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  ยกเลิก
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleTriggerBatchTransfer}
                >
                  ยืนยันโอนเงิน
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && batchResult && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">ผลการโอนเงิน</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setShowResultModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <strong>Batch Code:</strong> {batchResult.batchCode}
                </div>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h6>ประมวลผล</h6>
                        <h3>{batchResult.processed}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-success text-white">
                      <div className="card-body text-center">
                        <h6>สำเร็จ</h6>
                        <h3>{batchResult.success}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-danger text-white">
                      <div className="card-body text-center">
                        <h6>ล้มเหลว</h6>
                        <h3>{batchResult.failed}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-primary text-white">
                      <div className="card-body text-center">
                        <h6>ยอดรวม</h6>
                        <h3>{moneyDisplay(batchResult.totalAmount)}</h3>
                      </div>
                    </div>
                  </div>
                </div>

                {batchResult.results && batchResult.results.length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="thead-light">
                        <tr>
                          <th>User ID</th>
                          <th>จำนวนเงิน</th>
                          <th>สถานะ</th>
                          <th>หมายเหตุ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchResult.results.map((result, idx) => (
                          <tr key={idx}>
                            <td>{result.userId}</td>
                            <td>{moneyDisplay(result.amount)}</td>
                            <td>
                              <span
                                className={`badge badge-${
                                  result.status === "success"
                                    ? "success"
                                    : "danger"
                                }`}
                              >
                                {result.status === "success"
                                  ? "สำเร็จ"
                                  : "ล้มเหลว"}
                              </span>
                            </td>
                            <td>{result.error || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowResultModal(false)}
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WithdrawPendingTransfer;
